/**
 * The MONEY side of coverage — a second aggregate, never a projection of hours.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY MONEY IS ITS OWN AGGREGATE AND NOT AN HOURS FIGURE TIMES A RATE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The obvious cheap design is to keep one number — person-hours — and project
 * money out of it through a loaded hourly rate. It is wrong in both directions
 * and neither failure is visible in the output:
 *
 * 1. A COST WITH NO HOURS BEHIND IT IS UNREPRESENTABLE. A per-system licence
 *    fee, a per-run inference bill, a seat someone pays for whether or not the
 *    automation saves a minute — none of them is a quantity of work, so a
 *    projection has nothing to project FROM and the figure simply does not
 *    exist. That is most of what the screen calls "cost".
 * 2. REVENUE IS NOT A FUNCTION OF SAVED WORK. "This agent closed €40k of
 *    upsell" is a business claim about outcomes, and deriving it from hours
 *    would make it move whenever somebody edited a handling time — a number
 *    changing for a reason that has nothing to do with what it measures.
 *
 * And an UNLABELLED projection has a third cost that is worse than either: it
 * makes ONE NUMBER LAUNDER AS ANOTHER. A reader shown "22,108 hours" and
 * "€518,518" side by side, with nothing marking the second as derived from the
 * first, reads the same measurement twice and takes the second for an
 * independent confirmation.
 *
 * ── THE ONE PROJECTION THAT EXISTS, AND WHY IT IS NOT HERE ──────────────────
 *
 * `coverage-savings-projection.ts` DOES re-express saved hours in money, at the
 * role's blended labour rate, because the ledger screen needs to say what the
 * saved work is worth. It answers the paragraph above rather than contradicting
 * it, by three properties this aggregate cannot supply:
 *
 * - it is called a projection in the CONTRACT, not only in the UI;
 * - it publishes the RATE it used, so the derivation is visible at the point of
 *   reading and the figure cannot pass for a measurement;
 * - it lives OUTSIDE {@link RoleCoverageMoneyTotals}, so nothing that reads the
 *   money totals picks it up, and no field added beside {@link revenue} or
 *   {@link cost} later inherits its meaning.
 *
 * That last property is why it is not a sixth field here, and it is the one to
 * defend: the totals below are amounts somebody modelled, and mixing a derived
 * figure into them would make the set mean two different things at once.
 *
 * So money is a SECOND SUM OVER THE SAME TRAVERSAL, with its own denominator,
 * its own admissibility checks, and its own named failures. The two aggregates
 * share the impact ROWS and share nothing else: a `MONEY` result can never
 * reach the hours numerator and a `TIME·PERSON` result can never reach a money
 * one, because each slot checks its own dimension. That is enforced by the
 * dimension vector rather than by a convention, which is the whole reason the
 * vector carries a MONEY axis.
 *
 * ── WHY THIS IS NOT A SEPARATE LEDGER MODULE ────────────────────────────────
 *
 * The honest objection to putting money here is that a coverage engine which
 * quietly becomes a P&L is a worse object than two smaller honest ones. That
 * objection is about CONFLATION, and conflation is exactly what the paragraph
 * above forbids. What a separate module would actually cost is concrete:
 *
 * - A SECOND PERIOD-BASIS AXIS. `€400 per month` has precisely the problem
 *   `person-hours per week` has, and `coverage-period.types.ts` already solves
 *   it at input binding. A second module either duplicates that axis — two
 *   annualisers, drifting — or imports it, which is co-location with extra
 *   indirection.
 * - A SECOND INTEGRITY RECORD the caller must remember to merge. `DEGRADED`
 *   propagates to the organisation rollup because ONE record carries it; two
 *   records mean a hand-written merge at every consumer, and the one that gets
 *   forgotten is the money one, which is the aggregate a customer reads first.
 * - A SECOND ROW PER SYSTEM. `RoleSystemImpact.roleResourceId` is unique, so a
 *   system has exactly one impact row. Two ledgers would need two rows that can
 *   disagree about which system they describe.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * MONEY IS A DOUBLE, IN MAJOR UNITS — and the usual rule does not bind here
 * ══════════════════════════════════════════════════════════════════════════
 *
 * "Never a float for money" is a rule about STORED BALANCES and SETTLED
 * AMOUNTS. This module has neither. Nothing here is persisted — the design is
 * compute-on-read, so the only thing stored is the MODEL — and nothing here is
 * ever paid, invoiced or reconciled. These are modelled estimates on a planning
 * screen, exactly as `22,108 hours returned a year` is.
 *
 * Adopting integer minor units would make this module WORSE, not safer:
 *
 * - The engine multiplies by non-integer factors as its ordinary business —
 *   `0.8` attribution, `0.2` hours per ticket, `46` working weeks. Minor units
 *   under `× 0.8` are not integers, so an integer representation forces a
 *   ROUNDING STEP MID-COMPUTATION. `coverage-formula.schema.ts` refuses `round`
 *   from the function catalog for exactly that reason: it "destroys information
 *   mid-computation and would weaken the determinism argument for nothing".
 * - A `Decimal` type would put a runtime dependency into frontend-safe code and
 *   replace six operations ECMA-262 specifies exactly with library semantics.
 *   The bit-identical browser/server guarantee in `coverage-evaluator.ts` is
 *   the entire reason this module lives in `@nexus/types`, and it rests on
 *   IEEE-754 doubles under a fixed operation order.
 *
 * A double holds every integer to 2^53. The screen's largest figure is
 * €518,518 — ten orders of magnitude of headroom even counted in cents.
 *
 * **The rule that DOES bind, and it is already the design: never persist a
 * computed money amount.** Persist the model; compute on read. A stored total
 * is where float error accumulates and where a stale figure outlives the model
 * that produced it. Neither is possible here.
 *
 * Rounding for display is the caller's, exactly as it is for the ratio, and
 * both sides must round identically or they will disagree on a number this
 * module agreed on.
 */
import { z } from "zod";

import { type CoverageDimension, CoverageDimensions } from "./coverage-dimension.types";
import type { CoverageEvaluationFailure } from "./coverage-evaluator";

/** The dimension every money term must reduce to. */
export const COVERAGE_MONEY_DIMENSION: CoverageDimension = CoverageDimensions.MONEY;

/**
 * An ISO 4217 alphabetic currency code — `EUR`, `USD`, `MAD`. Never a symbol.
 *
 * A symbol is ambiguous ("$" is at least five different currencies) and is a
 * rendering concern; the code is the identity. Stored as the code, rendered as
 * whatever the reader's locale makes of it.
 *
 * ── WHY A SHAPE CHECK AND NOT A 180-VALUE ENUM ──────────────────────────────
 *
 * ISO 4217 gains and withdraws codes. A closed enum would refuse a customer's
 * legitimate currency until somebody shipped a deploy — a hard failure on a
 * correct input. A well-formed code that is not a real currency costs a wrong
 * LABEL and nothing else, because this engine never converts between
 * currencies: one organisation has one currency, every money quantity in every
 * one of its models is already stated in it, and there is no exchange rate
 * anywhere in the module. So the blast radius of the looser check is a string
 * on a screen, and the blast radius of the tighter one is a customer who cannot
 * use the feature.
 *
 * The database CHECK constraint on `OrganizationAutomationSettings.currency`
 * mirrors this pattern exactly, so a row cannot hold something this refuses.
 */
export const CoverageCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/, {
  message:
    "currency must be an ISO 4217 alphabetic code in upper case, such as EUR — never a symbol"
});

/** An ISO 4217 alphabetic currency code. */
export type CoverageCurrencyCode = z.infer<typeof CoverageCurrencyCodeSchema>;

/**
 * Why a role has no money figures at all.
 *
 * One arm today, and a union rather than a boolean for the same reason
 * {@link import("./coverage-ratio").CoverageRatio} is a union: the reason is
 * what an authoring UI renders, and a caller that has to narrow cannot lose it
 * by writing `?? 0`.
 */
export type CoverageMoneyNotModelledReason =
  /**
   * The organisation has never stated its currency, so no money quantity has an
   * identity yet.
   *
   * Deliberately not defaulted to anything. The working-time model refuses a
   * default because 46 working weeks against 52 moves every percentage by 13%
   * invisibly; a defaulted currency is the same failure with a bigger number
   * attached — it would price a US customer's ROI in euros and say nothing.
   */
  "NO_CURRENCY";

/**
 * The two money terms a system may declare.
 *
 * Both are money and they are NOT one signed column. A net of zero built from
 * `€0` revenue and `€0` cost, and one built from `€400` and `€400`, are
 * different facts about a system, and the screen renders them in separate
 * columns. Collapsing them would also make "this system declares no revenue
 * model" unrepresentable, which is a state {@link CoverageMoneyTerm} exists to
 * carry.
 */
export const COVERAGE_MONEY_TERMS = ["revenue", "cost"] as const;

/** `revenue` or `cost` — which money term of a system a warning is about. */
export type CoverageMoneyTermName = (typeof COVERAGE_MONEY_TERMS)[number];

/**
 * One money term of one system: authored and evaluated, authored and broken, or
 * never authored at all.
 *
 * THREE STATES, NOT A NULLABLE NUMBER. "This system declares no revenue model"
 * and "this system's revenue model does not evaluate" are different facts with
 * different fixes, and collapsing them into `null` is the sentinel this module
 * refuses everywhere else — see the null guardrail in `coverage-ratio.ts`. A
 * `null` here would additionally be indistinguishable from a modelled zero.
 */
export type CoverageMoneyTerm =
  /** No model was authored for this term. Contributes nothing, and that is correct. */
  | { readonly kind: "absent" }
  /** An amount in the organisation's currency, per year or absolute. */
  | { readonly kind: "amount"; readonly amount: number }
  /**
   * A model was authored and could not be counted. Contributes zero AND names
   * itself in `integrity.warnings`, exactly as a broken hours model does.
   *
   * `failure` is populated only when the model failed to EVALUATE. A model that
   * evaluated to the wrong dimension or an inadmissible period basis has no
   * evaluation failure to report — it computed a perfectly good number that is
   * not money — so `failure` is `null` and the warning carries the reason.
   */
  | { readonly kind: "failed"; readonly failure: CoverageEvaluationFailure | null };

/**
 * The money figures of one role, all in one currency.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY REVENUE HAS TWO ENTRY POINTS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A system-attached model cannot express revenue that no system produces — a
 * retention call a person holds, an upsell share agreed with sales. That is real
 * revenue of the role and it has no `RoleResource` to hang off, so `RoleWorkload`
 * carries a revenue model of its own and {@link workloadRevenue} reports it.
 * Without that column the state is not merely unreported, it is inexpressible.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * FOUR FIGURES, AND NO ROLLED-UP TOTAL. THAT IS DELIBERATE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * There is no `roleRevenue`, no `netCost` and no projected saving here, and none
 * of the three is an oversight.
 *
 * A ROLE-WIDE NET COST was specified and then withdrawn by its author as not
 * meaning what its name said. Shipping the field anyway "for later" is the worst
 * available option: a contract field is a promise that somebody will wire it up,
 * and the next person to do so would wire up a definition nobody holds.
 *
 * A ROLE-WIDE REVENUE TOTAL is one addition of two fields this object already
 * carries, so a caller reproduces it exactly and there is nothing for the engine
 * to add.
 *
 * A PROJECTED SAVING is a member of a different category — hours re-expressed
 * rather than money modelled — and it ships as `RoleCoverage.savingsProjection`,
 * beside this object rather than inside it. The header states the argument.
 *
 * ⚠️ **AND THE SAME IS TRUE OF {@link net}, WHICH IS WHY THE FLOAT ARGUMENT FOR
 * IT IS WRONG.** It was justified here as "a subtraction of two accumulators
 * whose summation ORDER is this engine's". That order is already spent by the
 * time anyone reads this object: `revenue` and `cost` ship as finished totals,
 * a JSON round-trip of a double is exact, and IEEE-754 subtraction of the same
 * two doubles is deterministic. A caller writing `totals.revenue - totals.cost`
 * gets the identical value, bit for bit. The original text conceded as much in
 * its own next clause and the caveat was then applied to a case it does not
 * cover.
 *
 * **`net` stays anyway, and for an honest reason rather than that one.** It is a
 * NAMED figure a previous slice shipped deliberately — the automation's P&L —
 * and it currently has no reader only because that slice's own screen has not
 * been built. That is "shipped ahead of its consumer", which is a different
 * state from the two fields removed above: one was invented in this change with
 * no request behind it, and the other was specified and then explicitly
 * withdrawn by its author. Neither had a future reader; this one has an unbuilt
 * one.
 *
 * If that screen never arrives, `net` is the next field to go, and the argument
 * will be this paragraph rather than a rediscovery.
 */
export interface RoleCoverageMoneyTotals {
  /**
   * What the role itself costs per year — salaries, seats.
   *
   * `null` when no cost model was authored, or when the one that was does not
   * evaluate to an admissible amount of money. The two are told apart by
   * `integrity.warnings`: the second names itself there, the first does not,
   * because a role nobody has costed is a normal state and not a broken model.
   *
   * Named `workloadCost` rather than `workload`: it is one of two money models
   * `RoleWorkload` now carries, and a bare `workload` beside `workloadRevenue`
   * reads as the pair's parent rather than as its cost half.
   */
  readonly workloadCost: number | null;
  /**
   * Revenue the ROLE holds that is attached to no system — read out of
   * `RoleWorkload`'s own revenue model.
   *
   * `null` when nobody authored one, or when the authored one was refused, told
   * apart by `integrity.warnings` exactly as {@link workloadCost} is. It is NOT
   * folded to zero here for the same reason: "no revenue has been modelled" and
   * "this role holds no revenue" are different claims and only one of them is
   * ours to make.
   *
   * This is the figure a ledger renders as revenue held by PEOPLE, because in
   * this model every euro attributable to a system is already in
   * {@link revenue}, so what remains is what the people still hold.
   */
  readonly workloadRevenue: number | null;
  /** Revenue the systems generate, summed over those that evaluated. */
  readonly revenue: number;
  /** What the systems cost, summed over those that evaluated. */
  readonly cost: number;
  /**
   * `revenue - cost` — what the attached SYSTEMS return, net of what they cost.
   *
   * The automation's own P&L, and deliberately blind to salaries: it answers "is
   * this automation worth what it costs to run", which is a question about the
   * systems and not about the role. **It is not the role's net cost and must
   * never be rendered as one** — it omits {@link workloadCost}, which is the
   * largest term in any real ledger, so the substitution produces a plausible
   * answer that is wrong by an order of magnitude.
   *
   * ⚠️ NO READER TODAY, and shipped computed for a reason the header corrects:
   * a caller subtracting the two published totals gets the identical double, so
   * this field saves nobody an error. It stays because a previous slice named
   * the concept deliberately and its screen is unbuilt — not because the engine
   * is the only thing that can compute it.
   */
  readonly net: number;
}

/** One role's money figures, or an explicit statement that there are none. */
export type RoleCoverageMoney =
  | { readonly kind: "not-modelled"; readonly reason: CoverageMoneyNotModelledReason }
  | {
      readonly kind: "modelled";
      readonly currency: CoverageCurrencyCode;
      readonly totals: RoleCoverageMoneyTotals;
    };

/** A money term that was never authored. The default state of every term. */
export const COVERAGE_MONEY_TERM_ABSENT: CoverageMoneyTerm = { kind: "absent" };
